// Recharts components
import {
  PieChart,
  Pie,
  Tooltip,
  Legend,
  Cell,
  ResponsiveContainer,
  Label,
} from 'recharts';

// Redux hooks and selectors
import { useAppSelector } from '../../redux/hooks/useAppSelector';
import { selectIsRTL } from '../../redux/selectors/displaySettingsSelectors';

// i18n hooks
import { useTranslation } from 'react-i18next';

// Custom hooks
import { useChartConfig } from './hooks/useChartConfig';
import { usePieActiveCell } from './hooks/usePieActiveCell';

// Shared components
import { ChartContainer } from './shared/ChartContainer';
import { ChartContent } from './shared/ChartContent';
import { ChartFooter } from './shared/ChartFooter';

interface PlatformData {
  name: string;
  value: number;
}

interface VerifiedUsersPieChartProps {
  data: PlatformData[];
}

export const VerifiedUsersPieChart = ({ data }: VerifiedUsersPieChartProps) => {
  const { t } = useTranslation();
  const isRTL = useAppSelector(selectIsRTL);
  const total = data.reduce((acc, curr) => acc + curr.value, 0);

  // Chart configuration
  const config = useChartConfig();

  // Hovered slice behavior
  const { getCellProps } = usePieActiveCell();

  return (
    <ChartContainer>
      {/* Header */}
      <div className="p-4 text-center">
        <h2 className="text-lg font-semibold text-gray-900">
          {t('dashboard.platformUsage')}
        </h2>
        <p className="text-sm text-gray-700">
          {t('dashboard.platforms.distribution')}
        </p>
      </div>

      {/* Chart */}
      <ChartContent>
        <style>
          {`
            .recharts-legend-item .recharts-surface {
              margin-${isRTL ? 'left' : 'right'}: 8px !important;
            }
          `}
        </style>
        <ResponsiveContainer
          width="100%"
          height={280}
          style={config.styles.noOutline}
        >
          <PieChart onClick={undefined} style={config.styles.noOutline}>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={100}
              dataKey="value"
              onClick={undefined}
              style={config.styles.noOutline}
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={
                    index === 0
                      ? config.colors.primary
                      : config.colors.secondary
                  }
                  {...getCellProps(index, {
                    suppressActive: data.length === 1 || entry.value === total,
                  })}
                />
              ))}
              {/* Center label */}
              <Label
                content={({ viewBox }) => {
                  if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                    return (
                      <text
                        x={viewBox.cx}
                        y={viewBox.cy}
                        textAnchor="middle"
                        dominantBaseline="central"
                      >
                        <tspan
                          x={viewBox.cx}
                          y={(viewBox.cy || 0) - 25}
                          className="fill-gray-900 text-2xl font-bold"
                        >
                          {total}
                        </tspan>
                        <tspan
                          x={viewBox.cx}
                          y={(viewBox.cy || 0) + 5}
                          className="fill-gray-700 text-sm"
                        >
                          {t('dashboard.dataLabels.users')}
                        </tspan>
                      </text>
                    );
                  }
                }}
              />
            </Pie>
            <Tooltip {...config.tooltip} />
            <Legend
              {...config.legend}
              iconType="rect"
              layout="horizontal"
              align="center"
              verticalAlign="bottom"
            />
          </PieChart>
        </ResponsiveContainer>
      </ChartContent>

      {/* Footer */}
      <ChartFooter>{t('dashboard.platforms.showing')}</ChartFooter>
    </ChartContainer>
  );
};
